<template>
    <div class="printers-settings">

        <v-skeleton-loader
            v-if="loading"
            transition-group="scale-transition"
            type="article, actions"
        />

        <template v-if="!loading">
            <v-alert
                v-if="device.platform === 'web'"
                class="printers-card"
                type="error"
                transition="slide-y-transition"
            >
                <div class="text-h6 pa-0 mb-3">{{ $t('printers.no_printers_detected') }}</div>
                <div>{{ $t('printers.printing_from_web') }}</div>

                <v-btn
                    v-if="device.operatingSystem === 'windows'"
                    class="mt-4 mr-2"
                    outlined
                    @click="openDownloadLink('windows')"
                >
                    <v-icon class="mr-2">fab fa-windows</v-icon>
                    {{ $t('printers.download_windows') }}
                </v-btn>

                <v-btn
                    v-else-if="device.operatingSystem === 'mac'"
                    class="mt-4 mr-2"
                    outlined
                    @click="openDownloadLink('mac')"
                >
                    <v-icon class="mr-2">fab fa-apple</v-icon>
                    {{ $t('printers.download_mac') }}
                </v-btn>

                <v-btn
                    v-if="false"
                    class="mt-4"
                    outlined
                    @click="openDownloadLink('android')"
                >
                    <v-icon class="mr-2">fab fa-android</v-icon>
                    {{ $t('printers.download_android') }}
                </v-btn>
            </v-alert>

            <template v-else>
                <v-card
                    v-if="(canBePrintServer || canPrintLocally) && !isInitiallyPrintServer"
                    class="printers-card"
                    :class="{'getstarted': getStarted}"
                    outlined
                >
                    <v-card-text>
                        <v-row v-if="canBePrintServer" no-gutters>
                            <v-col cols="12" sm="8">
                                <div class="mr-5 text-subtitle-1">{{ $t('printers.is_main_print_device') }}</div>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-btn-toggle
                                    v-model="curPrintConfig.local.isPrintServer"
                                    class="mt-1 wfull d-flex"
                                    mandatory
                                >
                                    <v-btn
                                        class="flex-1"
                                        text
                                        :value="true"
                                        :disabled="terminal?.isOrderingDevice()"
                                        @click="setPrintServer(true)"
                                    >
                                        {{ $t('general.yes') }}
                                    </v-btn>
                                    <v-btn
                                        class="flex-1"
                                        text
                                        :value="false"
                                        :disabled="terminal?.isOrderingDevice()"
                                        @click="setPrintServer(false)"
                                    >
                                        {{ $t('general.no') }}
                                    </v-btn>
                                </v-btn-toggle>
                            </v-col>
                        </v-row>

                        <v-divider v-if="canBePrintServer && canPrintLocally" class="my-5" />

                        <v-row v-if="canPrintLocally" no-gutters>
                            <v-col cols="12" sm="8">
                                <div class="mr-5 text-subtitle-1">{{ $t('printers.use_main_device') }}</div>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-btn-toggle
                                    v-model="curPrintConfig.local.printLocally"
                                    class="mt-1 wfull d-flex"
                                    mandatory
                                >
                                    <v-btn
                                        class="flex-1"
                                        text
                                        :value="false"
                                        :disabled="terminal?.isOrderingDevice()"
                                        @click="setPrintLocally(false)"
                                    >
                                        {{ $t('general.yes') }}
                                    </v-btn>
                                    <v-btn
                                        class="flex-1"
                                        text
                                        :value="true"
                                        :disabled="terminal?.isOrderingDevice()"
                                        @click="setPrintLocally(true)"
                                    >
                                        {{ $t('general.no') }}
                                    </v-btn>
                                </v-btn-toggle>
                            </v-col>
                        </v-row>

                    </v-card-text>
                </v-card>

                <!-- If we print trough main printing device -->
                <template v-if="!isPrintingLocally && !isPrintServer">
                    <v-alert
                        v-if="printServerStatus === 'not_set_up'"
                        class="printers-card"
                        type="error"
                        transition="slide-y-transition"
                    >
                        <div class="text-h6 pa-0 mb-3">{{ $t('printers.main_print_device_not_found') }}</div>
                        <div>{{ $t('printers.main_print_device_not_found_info') }}</div>

                        <v-btn
                            v-if="device.operatingSystem === 'windows'"
                            class="mt-4 mr-2"
                            outlined
                            @click="openDownloadLink('windows')"
                        >
                            <v-icon class="mr-2">fab fa-windows</v-icon>
                            {{ $t('printers.download_windows') }}
                        </v-btn>

                        <v-btn
                            v-else-if="device.operatingSystem === 'mac'"
                            class="mt-4 mr-2"
                            outlined
                            @click="openDownloadLink('mac')"
                        >
                            <v-icon class="mr-2">fab fa-apple</v-icon>
                            {{ $t('printers.download_mac') }}
                        </v-btn>
                    </v-alert>

                    <v-alert
                        v-else-if="printServerStatus === 'not_connected'"
                        class="printers-card"
                        type="error"
                        transition="slide-y-transition"
                    >
                        <div class="text-h6 pa-0 mb-3">{{ $t('printers.main_print_device_not_found') }}</div>
                        <div>{{ $t('printers.main_print_device_connection_failed') }}</div>
                    </v-alert>

                    <v-alert
                        v-else-if="printServerStatus === 'connected'"
                        class="printers-card"
                        type="success"
                        transition="slide-y-transition"
                    >
                        <div class="text-h6 pa-0 mb-3">{{ $t('printers.main_print_device_found') }}</div>
                        <div>{{ $t('printers.printing_on_main_print_device') }}</div>
                    </v-alert>

                </template>

                <v-card
                    v-if="isPrintingLocally || isPrintServer"
                    class="printers-card printers"
                    :class="{'getstarted': getStarted}"
                    outlined
                >
                    <v-card-text v-if="form" class="content">

                        <v-alert
                            v-if="isPrintingLocally"
                            class="mb-7"
                            type="info"
                            transition="slide-y-transition"
                        >
                            {{ $t('printers.set_up_local_print_config') }}
                        </v-alert>

                        <v-alert
                            v-if="isPrintServer"
                            class="mb-7"
                            type="info"
                            transition="slide-y-transition"
                        >
                            {{ $t('printers.main_print_device_description') }}
                        </v-alert>

                        <v-combobox
                            v-for="(type, index) in printerTypes"
                            :key="index"
                            v-model="form[type]"
                            outlined
                            hide-details
                            hide-selected
                            class="pb-5"
                            :items="getSelectablePrinters(type)"
                            item-value="printer.name"
                            item-text="printer.description"
                            clearable
                            clear-icon="fal fa-times"
                            :label="$t(`printers.${type}`)"
                            :placeholder="$t(`printers.${type}_printer_placeholder`)"
                            :no-data-text="$t('printers.no_printers_detected')"
                            :append-icon="form[type] ? 'fal fa-cog' : ''"
                            @click:append="showPrinterSettings(type)"
                        />
                    </v-card-text>

                    <v-card-actions v-if="form" class="actions">
                        <v-btn
                            v-if="!getStarted"
                            class="minw100 wfull"
                            :disabled="form.busy || disableSave || busy"
                            :loading="form.busy || busy"
                            color="primary"
                            depressed
                            @click="savePrinters"
                        >
                            {{ $t('general.save') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </template>
        </template>

        <v-dialog
            v-model="printerSettings"
            max-width="450"
        >
            <div class="primary pa-4 d-flex">
                <v-icon class="mr-4 white--text" size="32">fal fa-print</v-icon>
                <span class="body-1 white--text mt-1">{{ $t('general.settings') }}</span>
            </div>

            <div v-if="selectedType && form[selectedType]" class="pa-5 white">
                <template v-if="form[selectedType].printer.name === 'fp1'">
                    <v-text-field
                        v-model="form[selectedType].options.other.requestPath"
                        :label="$t('printers.request_path')"
                        placeholder="C:\tring\xml"
                        outlined
                    />

                    <v-text-field
                        v-model="form[selectedType].options.other.responsePath"
                        :label="$t('printers.response_path')"
                        placeholder="C:\tring\xml\odgovori"
                        outlined
                    />
                </template>

                <template v-else>
                    <v-text-field
                        v-if="form[selectedType].printer.name === 'network_printer'"
                        v-model="form[selectedType].options.ipAddress"
                        :label="$t('printers.address')"
                        placeholder="10.10.10.10:1234"
                        outlined
                    />

                    <template v-if="device.platform === 'android'">
                        <number-field
                            v-model="form[selectedType].options.width"
                            :label="$t('printers.width_mm')"
                            :precision="0"
                            outlined
                        />

                        <number-field
                            v-model="form[selectedType].options.charsPerLine"
                            :label="$t('printers.chars_per_line')"
                            :precision="0"
                            outlined
                        />

                        <number-field
                            v-model="form[selectedType].options.dpi"
                            :label="$t('printers.dpi')"
                            :precision="0"
                            outlined
                        />

                        <number-field
                            v-model="form[selectedType].options.qrCodeSize"
                            :label="$t('printers.qr_code_size_mm')"
                            :precision="0"
                            outlined
                        />
                    </template>

                    <template v-else-if="form[selectedType].options.escpos">
                        <number-field
                            v-model="form[selectedType].options.width"
                            :label="$t('printers.width_percent')"
                            :precision="0"
                            suffix="%"
                            outlined
                        />

                        <number-field
                            v-model="form[selectedType].options.charsPerLine"
                            :label="$t('printers.chars_per_line')"
                            :precision="0"
                            outlined
                        />

                        <number-field
                            v-model="form[selectedType].options.qrCodeSize"
                            :label="$t('printers.qr_code_size_percent')"
                            :precision="0"
                            suffix="%"
                            outlined
                        />
                    </template>

                    <template v-else-if="!form[selectedType].options.escpos">
                        <v-select
                            v-model="paperSize"
                            :items="paperSizes"
                            item-text="name"
                            return-object
                            :label="$t('printers.paper_size')"
                            outlined
                            @input="setPaperSize"
                        />

                        <number-field
                            v-model="form[selectedType].options.width"
                            :label="$t('printers.width')"
                            :disabled="!paperSize || !paperSize.editableWidth"
                            :precision="0"
                            suffix="mm"
                            outlined
                        />

                        <number-field
                            v-model="form[selectedType].options.height"
                            :label="$t('printers.length')"
                            :disabled="!paperSize || !paperSize.editableHeight"
                            :precision="0"
                            suffix="mm"
                            outlined
                        />

                        <number-field
                            v-model="form[selectedType].options.margin"
                            :label="$t('printers.margin')"
                            :precision="0"
                            suffix="mm"
                            outlined
                        />
                    </template>
                </template>

                <v-btn
                    color="primary"
                    block
                    outlined
                    :disabled="form[selectedType].printer.name === 'network_printer' && !validateIpAddress(form[selectedType].options.ipAddress)"
                    @click="testPrinter(form[selectedType])"
                >
                    Test
                </v-btn>
            </div>
        </v-dialog>
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import PrintService from '@/print/PrintService';
import NumberField from '@/components/NumberField';
import FiscalServiceLegacy from '@/services/FiscalServiceLegacy';
import Terminal from '@/models/Terminal';
import AppEvent from '@/models/AppEvent';

export default {
    components: {
        NumberField
    },

    props: {
        predefinedPrinters: {
            type: Array,
            default: () => [],
        },
        serverPrintConfig: {
            type: Object,
            default: () => null,
        },
        getStarted: {
            type: Boolean,
            default: false,
        },
    },

    created() {
        this.form = this.createForm();
    },

    async mounted() {
        await PrintService.init()

        this.loading = true;
        setTimeout(() => {
            PrintService.detectPrinters()
                .then(data => {
                    this.loading = false;
                    this.detectedPrinters = data.detectedPrinters;
                    this.printServerStatus = data.printServerStatus;
                }).catch(error => {
                    console.log(error.message)
                    this.loading = false;
                })
        }, 1000)

        this.terminal = await Terminal.current()
    },

    data() {
        return {
            loading: false,
            busy: false,
            detectedPrinters: null,
            form: null,
            curPrintConfig: PrintService.printConfig,
            isInitiallyPrintServer: PrintService.printConfig.local.isPrintServer,
            printServerStatus: null,

            printerSettings: false,
            selectedType: null,
            printerTypes: [
                'receipt',
                'order',
                'kitchen',
                'bar',
                'sector_1',
                'sector_2',
            ],

            paperSizes: [
                { name: this.$t('printers.roll_paper') + ' 58mm', width: 58, height: 297, editableWidth: false, editableHeight: true },
                { name: this.$t('printers.roll_paper') + ' 80mm', width: 80, height: 297, editableWidth: false, editableHeight: true },
                { name: 'A4', width: 210, height: 297, editableWidth: false, editableHeight: false },
                { name: this.$t('general.other'), width: null, height: null, editableWidth: true, editableHeight: true },
            ],

            paperSize: null,
            terminal: null
        };
    },

    computed: {
        canPrintLocally() {
            return this.device.platform !== 'web' &&
                this.curPrintConfig &&
                !this.curPrintConfig.local.isPrintServer;
        },

        isPrintingLocally() {
            return this.curPrintConfig && this.curPrintConfig.local.printLocally;
        },

        canBePrintServer() {
            return this.device.platform === 'electron';
        },

        isPrintServer() {
            return this.curPrintConfig && this.curPrintConfig.local.isPrintServer;
        },

        printers() {
            const networkPrinter = {
                printer: { name: 'network_printer', description: this.$t('printers.network') },
                options: {
                    id: null,
                    escpos: true,
                    width: 100,
                    height: null,
                    margin: null,
                    charsPerLine: 48,
                    dpi: 203,
                    ipAddress: null,
                    qrCodeSize: 200,
                    other: {},
                    version: 1
                }
            }

            if(this.isPrintingLocally || this.isPrintServer) {
                return this.detectedPrinters
                    ? [ ...this.detectedPrinters.local, networkPrinter ]
                    : [ networkPrinter ]
            }

            return this.detectedPrinters ? this.detectedPrinters.remote : []
        },

        disableSave() {
            const networkPrinters = Object.values(this.form.toJson())
                .filter(printer => !!printer && printer.printer && printer.printer.name === 'network_printer')

            let disable = false

            networkPrinters.forEach(printer => {
                if(!this.validateIpAddress(printer.options.ipAddress)) {
                    disable = true
                }
            })

            return disable
        },
    },

    methods: {
        createForm() {
            const printers = this.curPrintConfig.local.printLocally
                ? this.curPrintConfig.local.printers
                : this.serverPrintConfig

            const formData = {}

            this.printerTypes.forEach(type => {
                const printerName = this.curPrintConfig.local.printLocally ? type : `printer_${type}`
                formData[type] = printers && printers[printerName] ? printers[printerName] : null
            })

            return new CodForm(formData);
        },

        getSelectablePrinters(type) {
            if(locale === 'ba') {
                return [{
                    printer: { name: 'fp1', description: 'Tring FP1 Plus' },
                    options: {
                        id: null,
                        escpos: false,
                        width: 100,
                        height: null,
                        margin: null,
                        charsPerLine: 32,
                        dpi: 203,
                        ipAddress: null,
                        qrCodeSize: 200,
                        other: {
                            requestPath: 'C:\\tring\\xml',
                            responsePath: 'C:\\tring\\xml\\odgovori',
                        },
                        version: 1
                    }
                }]
            }

            return JSON.parse(JSON.stringify(this.printers))
        },

        setPrintServer(value) {
            this.curPrintConfig.local.isPrintServer = value;

            if (!value) {
                PrintService.configure(this.curPrintConfig);
                this.form = this.createForm();
                return;
            }

            this.curPrintConfig.local.printLocally = false;
            PrintService.configure(this.curPrintConfig);
            PrintService.setAsMainPrintServer();
            this.form = this.createForm();

            PrintService.detectPrinters()
                .then(data => {
                    this.detectedPrinters = data.printers;
                    this.printServerStatus = data.printServerStatus;
                })
        },

        setPrintLocally(value) {
            this.curPrintConfig.local.printLocally = value;
            PrintService.configure(this.curPrintConfig);
            this.form = this.createForm();
        },

        showPrinterSettings(type) {
            this.selectedType = type
            this.printerSettings = true

            const width = this.form[this.selectedType].options.width
            const height = this.form[this.selectedType].options.height
            if(width) {
                this.paperSize = this.paperSizes.find(paper => {
                    return paper.width == width
                        && (paper.editableHeight || (!paper.editableHeight && paper.height == height))
                })
            }

            if(!this.paperSize) {
                this.paperSize = this.paperSizes[this.paperSizes.length - 1]
            }
        },

        savePrinters() {
            this.busy = true

            return new Promise((resolve, reject) => {
                if(!this.isPrintingLocally && !this.isPrintServer) {
                    resolve();
                    return;
                }

                if(this.isPrintingLocally) {
                    this.curPrintConfig.local.printers = this.form.toJson();
                    PrintService.configure(this.curPrintConfig);
                    FiscalServiceLegacy.init().then(() => {
                        this.$snackbar(this.$tc('printers.printers_updated', 2), 'success');
                        this.busy = false
                        resolve()
                    })
                    return;
                }

                ApiService.patch('/print/config', this.form).then(response => {
                    AppEvent.build('printersUpdate', this.form.toJson()).send()

                    PrintService.syncConfigWithServer().then(config => {
                        this.curPrintConfig = config;
                        FiscalServiceLegacy.init().then(() => {
                            this.$snackbar(this.$tc('printers.printers_updated', 2), 'success');
                            this.busy = false
                            resolve()
                        })
                    });
                });
            })
        },

        async testPrinter(printer) {
            const srTestLines = [
                'a b c č ć d dž đ e f g h i j k',
                'lj m n nj o p r s š t u v z ž',
                'A B C Č Ć D DŽ Đ E F G H I J K',
                'LJ M N NJ O P R S Š T U V Z Ž',
                'а б в г д ђ е ж з и ј к л љ м н',
                'њ о п р с т ћ у ф ч џ ш',
                'А Б В Г Д Ђ Е Ж З И Ј К Л Љ М Н',
                'Њ О П Р С Т Ћ У Ф Ч Џ Ш',
            ]

            const html = '<div style="word-break: break-word; border: 1px solid black;">' +
                '<div>123456789_123456789_123456789_123456789_123456789_</div>' +
                (['me', 'sr', 'ba'].includes(locale) ? srTestLines.map(l => `<div>${l}</div>`).join('') : '') +
            '</div>'

            const text = '123456789_123456789_123456789_123456789_123456789_\n' +
                (['me', 'sr', 'ba'].includes(locale) ? srTestLines.join('\n') : '')

            const qrCode = 'TEST QR CODE'

            if(printer?.printer?.name === 'fp1') {
                await FiscalServiceLegacy.setup(printer)
                FiscalServiceLegacy.printText('\n' + text + '\n', true)
                return
            }

            PrintService.sendToPrinter({ ...printer, connection: 'local' }, html, text, qrCode)
        },

        openDownloadLink(platform) {
            if(platform === 'windows' || platform === 'mac') {
                let releasesRepo = 'nikolalj/' + `${isAllegra ? 'allegra' : 'aria'}-release` + (locale === 'sr' ? '-sr' : '')
                ApiService.get(`https://api.github.com/repos/${releasesRepo}/releases/latest`, {
                    transformRequest: [(data, headers) => {
                        headers.common = {};
                        return data;
                    }],
                }).then(response => {
                    const execFile = response.data.assets.find(asset => asset.name.endsWith(platform === 'mac' ? '.dmg' : '.exe'))
                    if(execFile) {
                        window.open(execFile.browser_download_url, '_blank');
                    }
                })
            } else if(platform === 'android') {
                const appId = isAllegra ? 'me.allegrapos.app' : 'me.ariapos.app'
                window.open(`https://play.google.com/store/apps/details?id=${appId}`, '_blank');
            }
        },

        validateIpAddress(ipAddress) {
            if(!ipAddress) return false

            const addressParts = ipAddress.split(':')
            if(addressParts.length !== 2) {
                return false
            }

            if (addressParts[0] !== 'localhost' && !/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(addressParts[0])) {
                return false
            }

            const port = addressParts[1]
            return /^[0-9]+$/.test(port)
        },

        setPaperSize(paperSize) {
            if(paperSize && this.form[this.selectedType] && this.form[this.selectedType].options) {
                this.form[this.selectedType].options.width = paperSize.width;
                this.form[this.selectedType].options.height = paperSize.height;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.printers-settings {

    .printers-card {
        max-width: 600px;
        margin-bottom: 20px;

        &.printers {
            &.theme--dark {
                background-color: #1e222b !important;
            }
        }

        &.getstarted {
            max-width: 100%;
            border: none;

            .content {
                padding: 0;
            }
        }

        .content {
            padding: 24px;
        }

        .actions {
            padding: 0 24px 24px;
        }
    }
}
</style>
